import React, { useEffect, useMemo, useState } from "react";
import { getTemplate, Participant, DocumentType } from "../../api/OneHrApiDocumentTemplate";
import {
  CATEGORY_LABELS,
  DEFAULT_API_KEY,
  DOCUMENT_TYPE_LABELS,
  PARTICIPANT_TYPE_LABELS,
  STATUS_MESSAGES,
  VALIDATION_MESSAGES,
} from "../constants";
import { Field, PlaceholderFormat as PlaceholderFormatType, SectionState, ValidationResponse } from "../types";
import {
  validatePlaceholderSyntax,
  validateFormat,
  extractPlaceholdersFromDocument,
  createFieldMap,
  transformTemplatePlaceholders,
  groupPlaceholdersByCategory,
  getApiKey,
} from "../utils/validation";
import { insertPlaceholderIntoDocument } from "../utils/wordHelpers";
import "../taskpane";

function FieldRow({
  field,
  count,
  onInsert,
}: {
  field: Field;
  count: number;
  onInsert: (field: Field) => void;
}) {
  // Check if this field has format options
  const hasFormatOptions = field.type === "DATE" || field.type === "NUMBER" || field.type === "CURRENCY";

  // const handleInsertPdfField = () => {
  //   const func = (window as any).onehrInsertPdfFormFieldDirect;
  //   if (func) {
  //     // Directly call with field.label and field.key
  //     func(field.label, field.key, field.category);
  //   }
  // };

  return (
    <div className="flex items-start justify-between gap-3 rounded border p-3 bg-white">
      <div>
        <div className="text-sm font-medium">{field.label}</div>
        <div className="text-xs text-gray-500 font-mono">{"{{" + field.key + "}}"}</div>
        {hasFormatOptions && (
          <div className="text-xs text-blue-600 mt-1">Формат сонгох боломжтой</div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onInsert(field)}
          className="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm"
          title="Түлхүүр оруулах"
        >
          {hasFormatOptions ? "Формат" : "Түлхүүр"}
        </button>
        {/* <button
          // field.key, field.label
          onClick={handleInsertPdfField}
          className="px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 text-sm"
          title="PDF форм талбар оруулах"
        >
          PDF
        </button> */}
        <span className="text-xs px-2 py-1 rounded bg-gray-100">{count}</span>
      </div>
    </div>
  );
}

function CardSection({
  title,
  children,
  defaultOpen = false,
  
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  useEffect(() => {
    setInternalOpen(defaultOpen);
  }, [defaultOpen]);
  return (
    <section className="mt-6">
      <button
        onClick={() => {
          setInternalOpen(!internalOpen);
        }}
        className="w-full flex items-center justify-between text-left"
      >
        <h2 className="text-base font-semibold">{title}</h2>
        <span className="text-gray-500">{internalOpen ? "▾" : "▸"}</span>
      </button>
      {internalOpen && <div className="mt-3 grid gap-3">{children}</div>}
    </section>
  );
}

export default function App() {
  const [sectionOpen, setSectionOpen] = useState<SectionState>({
    connect: true,
    contract: false,
    personal: false,
    job: false,
    salary: false,
    company: false,
    request: false,
    system: false,
    decree: false,
  });
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [template, setTemplate] = useState<any>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [placeholders, setPlaceholders] = useState<Field[]>([]);
  const [placeholdersByCategory, setPlaceholdersByCategory] = useState<Record<string, Field[]>>({});
  const [status, setStatus] = useState<string>("");
  const [templateCode, setTemplateCode] = useState<string>("");
  const [documentType, setDocumentType] = useState<DocumentType>("contract");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [formatDialogOpen, setFormatDialogOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<Field | null>(null);
console.log("Rendering App component with participants:", participants);
  const validatePlaceholders = async (): Promise<ValidationResponse> => {
    const errors: string[] = [];

    try {
      // Validate template is loaded
      if (!template) {
        errors.push("Загвар ачаалаагүй байна. Эхлээд загварыг холбоно уу.");
        return {
          isValid: false,
          errors,
        };
      }

      // Extract all placeholders from the document
      const documentPlaceholders = await extractPlaceholdersFromDocument();

      if (documentPlaceholders.length === 0) {
        // No placeholders found - this might be intentional, so just warn
        return {
          isValid: true,
          errors: [],
        };
      }

      // Debug logging
      console.log("Document placeholders found:", documentPlaceholders);
      console.log("Document placeholders count:", documentPlaceholders.length);
      console.log("Unique placeholders:", Array.from(new Set(documentPlaceholders)));

      // Create field map for quick lookup
      const fieldMap = createFieldMap(placeholders, dynamicParticipantFields);

      // Get all valid placeholder keys for existence checking
      const allValidPlaceholders = Array.from(fieldMap.keys());

      // Debug logging
      console.log(
        "Global placeholders available:",
        placeholders.map((f) => f.key)
      );
      console.log(
        "Participant placeholders available:",
        dynamicParticipantFields.flat(1).map((f) => f.key)
      );
      console.log("All valid placeholders:", allValidPlaceholders);
      console.log("Total valid placeholders count:", allValidPlaceholders.length);

      // Track required fields that are missing
      const requiredFields = new Set<string>();
      placeholders.forEach((field) => {
        if (field.required && field.key) {
          requiredFields.add(field.key);
        }
      });
      dynamicParticipantFields.forEach((participantFields) => {
        participantFields.forEach((field) => {
          if (field.required && field.key) {
            requiredFields.add(field.key);
          }
        });
      });

      const foundPlaceholders = new Set<string>();

      // Validate each placeholder
      for (const placeholder of documentPlaceholders) {
        // Step 1: Validate syntax
        const syntaxValidation = validatePlaceholderSyntax(placeholder);
        if (!syntaxValidation.valid) {
          errors.push(syntaxValidation.error || `Буруу синтакс: "${placeholder}"`);
          continue;
        }

        // Step 2: Extract the field name from {{fieldName}} or {{fieldName|format}}
        const fullFieldName = placeholder.replace(/^\{\{|\}\}$/g, "");

        // Strip pipe syntax to get base key (e.g., "contract.effectiveDate|mongolian_long" -> "contract.effectiveDate")
        const [baseFieldName, format] = fullFieldName.split("|").map((s) => s.trim());

        // Step 3: Check if base field exists in template
        if (!allValidPlaceholders.includes(baseFieldName)) {
          errors.push(
            `Үл мэдэгдэх түлхүүр: "${placeholder}". "${baseFieldName}" талбар загварт тодорхойлогдоогүй байна.`
          );
          continue;
        }

        // Step 4: Validate format if present
        if (format) {
          const field = fieldMap.get(baseFieldName);
          if (field) {
            const formatValidation = validateFormat(format, field.type);
            if (!formatValidation.valid) {
              errors.push(
                formatValidation.error ||
                  `Буруу формат: "${placeholder}". "${field.type}" төрлийн талбарт зөв формат сонгоно уу.`
              );
            }
          }
        }

        // Track that this placeholder was found
        foundPlaceholders.add(baseFieldName);
      }

      return {
        isValid: errors.length === 0,
        errors,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Тодорхойгүй алдаа";
      console.error("Placeholder validation error:", error);
      errors.push(`Баталгаажуулалтын алдаа: ${errorMessage}`);
      return {
        isValid: false,
        errors,
      };
    }
  };

  const handleValidate = async () => {
    try {
      setStatus(STATUS_MESSAGES.VALIDATING);
      setValidationErrors([]);

      const validation = await validatePlaceholders();

      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setStatus(`${VALIDATION_MESSAGES.VALIDATION_FAILED}: ${validation.errors.length} алдаа олдлоо`);
      } else {
        setStatus(VALIDATION_MESSAGES.VALIDATION_SUCCESS);
        setValidationErrors([]);

        setTimeout(() => {
          setStatus(STATUS_MESSAGES.READY_TO_VALIDATE);
        }, 3000);
      }
    } catch (error) {
      const errorMessage = `Баталгаажуулалтын алдаа: ${error instanceof Error ? error.message : "Тодорхойгүй алдаа"}`;
      setStatus(errorMessage);
      setValidationErrors([errorMessage]);
    }
  };

  const handleSave = async () => {
    try {
      setStatus(STATUS_MESSAGES.VALIDATING);
      setValidationErrors([]);

      const validation = await validatePlaceholders();

      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setStatus(`${VALIDATION_MESSAGES.VALIDATION_FAILED}: ${validation.errors.length} алдаа олдлоо`);
        return;
      }

      setStatus(STATUS_MESSAGES.SAVING);

      // Here you would implement the actual save logic
      setStatus(VALIDATION_MESSAGES.SAVE_SUCCESS);
      setValidationErrors([]);

      setTimeout(() => {
        setStatus(STATUS_MESSAGES.READY_TO_SAVE);
      }, 3000);
    } catch (error) {
      const errorMessage = `${VALIDATION_MESSAGES.SAVE_FAILED}: ${error instanceof Error ? error.message : "Тодорхойгүй алдаа"}`;
      setStatus(errorMessage);
      setValidationErrors([errorMessage]);
    }
  };

  // Handle field insertion
  const handleFieldInsert = (field: Field) => {
    // Check if field type requires format selection
    if (field.type === "DATE" || field.type === "NUMBER" || field.type === "CURRENCY") {
      // Show format dialog
      setSelectedField(field);
      setFormatDialogOpen(true);
    } else {
      // Insert directly without format
      insertPlaceholder(field.key);
    }
  };

  // Insert placeholder with optional format
  const insertPlaceholder = async (key: string, format?: PlaceholderFormatType) => {
    const field =
      placeholders.find((f) => f.key === key) ||
      dynamicParticipantFields.flat(1).find((f) => f.key === key);

    const result = await insertPlaceholderIntoDocument(key, format, field?.type);
    setStatus(result.message);
  };

  // Handle format dialog close
  const handleFormatDialogClose = () => {
    setFormatDialogOpen(false);
    setSelectedField(null);
  };

  // Handle format selection and insertion
  const handleFormatInsert = (format: PlaceholderFormatType) => {
    if (selectedField) {
      insertPlaceholder(selectedField.key, format);
      handleFormatDialogClose();
    }
  };

  const loadFromBackend = async () => {
    const currentTemplateCode =
      templateCode || (document.getElementById("template-code-input") as HTMLInputElement)?.value;
    let currentApiKey = (document.getElementById("onehr-api-key") as HTMLInputElement)?.value;

    if (!currentTemplateCode) {
      setStatus(STATUS_MESSAGES.NO_TEMPLATE_CODE);
      return;
    }

    // Get API key from input or environment
    currentApiKey = getApiKey(currentApiKey);
    if (document.getElementById("onehr-api-key")) {
      (document.getElementById("onehr-api-key") as HTMLInputElement).value = currentApiKey;
    }

    try {
      setStatus(STATUS_MESSAGES.LOADING_TEMPLATE);

      // Load template data with document type (for participants and metadata)
      const templateData = await getTemplate(currentTemplateCode, currentApiKey, documentType);
      setTemplate(templateData);

      // Extract participants from the template data
      // Handle different participant field names based on document type
      let templateParticipants: Participant[] = [];
      
      if (documentType === "job-description") {
        // Job descriptions use SignParticipants
        templateParticipants = templateData?.CurrentVersion?.SignParticipants || [];
      } else {
        // Contract and decree use Participants
        templateParticipants = templateData?.CurrentVersion?.Participants || [];
      }
      
      setParticipants(templateParticipants);

      // Extract global placeholders from template's documentFields
      const documentFields = templateData?.CurrentVersion?.documentFields;
      let templatePlaceholders: any[] = [];

      if (documentFields) {
        const fields =
          typeof documentFields === "string" ? JSON.parse(documentFields) : documentFields;
        templatePlaceholders = fields?.globalPlaceholders || [];

        console.log("Document fields from template:", fields);
        console.log("Global placeholders from template:", templatePlaceholders);
      }

      // Transform and sort placeholders
      const placeholderFields = transformTemplatePlaceholders(templatePlaceholders);
      setPlaceholders(placeholderFields);

      // Group placeholders by category
      const categorized = groupPlaceholdersByCategory(placeholderFields);
      setPlaceholdersByCategory(categorized);

      // Close the connection section after successful load
      setSectionOpen((prev) => ({ ...prev, connect: false }));

      if (templateParticipants.length > 0) {
        setStatus(
          `${placeholderFields.length} түлхүүр болон ${templateParticipants.length} оролцогч амжилттай ачааллаа.`
        );
      } else {
        setStatus(`${placeholderFields.length} түлхүүр ачааллаа. Загварт оролцогч олдсонгүй.`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Тодорхойгүй алдаа гарлаа";
      setStatus(`Загвар ачаалахад алдаа гарлаа: ${errorMessage}`);
      console.error("Error loading template:", error);
    }
  };

  // Dynamically derive participant placeholders for fetched participants
  const dynamicParticipantFields: Field[][] = useMemo(() => {
    if (!participants || participants.length === 0) return [];

    // Separate participants by type to calculate type-specific indices
    const employeeParticipants = participants.filter(
      (p) => String(p?.type).toUpperCase() === "EMPLOYEE"
    );
    const orgParticipants = participants.filter(
      (p) => String(p?.type).toUpperCase() === "ORGANIZATION"
    );

    return participants.map((p, i) => {
      const isOrg = String(p?.type).toUpperCase() === "ORGANIZATION";

      // Prefer backend-provided fields if available
      const provided = Array.isArray(p?.Fields)
        ? p.Fields.sort((a, b) => (a.sort || 0) - (b.sort || 0)).map((f) => {
            return {
              key: f.key,
              sort: f.sort,
              label: f.label || f.key,
              category: f.category || "personal",
              type: f.type || "TEXT",
              required: f.required || false,
            } as Field;
          })
        : [];

      if (provided.length > 0) return provided;

      // No fallback fields - only use backend-provided fields
      return [];
    });
  }, [participants]);

  // Recompute usage counts whenever notified from the Office layer
  useEffect(() => {
    const handler = async () => {
      // Build a flat string[] of all field keys (both participants and general placeholders)
      const placeholderKeys = placeholders
        .map((f) => f.key)
        .filter((k): k is string => typeof k === "string" && k.length > 0);
      const dynamicKeys = dynamicParticipantFields
        .flat(1)
        .map((f) => f.key)
        .filter((k): k is string => typeof k === "string" && k.length > 0);
      const keys = [...placeholderKeys, ...dynamicKeys];
      const fn = window.onehrGetUsageCountsForKeys;
      if (fn) {
        try {
          const map = await fn(keys);
          if (map && typeof map === "object") {
            setCounts(map);
          }
        } catch {}
      }
    };
    window.addEventListener("onehr:usage-updated", handler as EventListener);
    // Also compute on first mount
    handler();
    return () => window.removeEventListener("onehr:usage-updated", handler as EventListener);
  }, [dynamicParticipantFields, placeholders]);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <header className="flex items-center gap-3 p-4 bg-white border-b">
        <img width={40} height={40} src="../../assets/logo-filled.png" alt="OneHR" />
        <h1 className="text-xl font-bold">OneHR Contract</h1>
      </header>
      <main className="p-4 py-0">
        <CardSection title="Загварын холболт OneHR Contract" defaultOpen={sectionOpen.connect}>
          <div className="rounded-lg border bg-white p-4">
            <div className="mt-3 grid gap-3">
              <div>
                <label htmlFor="document-type-select" className="block text-sm mb-1">
                  Баримтын төрөл
                </label>
                <select
                  id="document-type-select"
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value as DocumentType)}
                  className="w-full border rounded px-2 py-1"
                >
                  <option value="contract">{DOCUMENT_TYPE_LABELS.contract}</option>
                  <option value="decree">{DOCUMENT_TYPE_LABELS.decree}</option>
                  <option value="job-description">{DOCUMENT_TYPE_LABELS["job-description"]}</option>
                </select>
              </div>
              <div>
                <label htmlFor="template-code-input" className="block text-sm mb-1">
                  Загварын код
                </label>
                <input
                  id="template-code-input"
                  value={templateCode}
                  onChange={(e) => setTemplateCode(e.target.value)}
                  className="w-full border rounded px-2 py-1"
                  placeholder="One HR created Template Code"
                />
              </div>
              <div>
                <label htmlFor="onehr-api-key" className="block text-sm mb-1">
                  Api Key
                </label>
                <input
                  id="onehr-api-key"
                  type="password"
                  className="w-full border rounded px-2 py-1"
                  placeholder="One HR company settings copy Key"
                  defaultValue={DEFAULT_API_KEY}
                />
              </div>
              <button
                onClick={loadFromBackend}
                className="px-3 py-1.5 rounded bg-blue-600 text-white"
              >
                Холбох
              </button>
              <div className="text-xs text-gray-600">{status}</div>
            </div>
          </div>
        </CardSection>

        {/* Template Info */}
        {template && (
          <CardSection title="Загварын мэдээлэл" defaultOpen={true}>
            <div className="p-3 bg-white border rounded-lg">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="font-medium">Төрөл:</div>
                <div>{DOCUMENT_TYPE_LABELS[documentType]}</div>
                <div className="font-medium">Нэр:</div>
                <div>{template.name}</div>
                <div className="font-medium">Код:</div>
                <div>{template.code}</div>
                <div className="font-medium">Төлөв:</div>
                <div>{template.status}</div>
                {template.description && (
                  <>
                    <div className="font-medium">Тайлбар:</div>
                    <div>{template.description}</div>
                  </>
                )}
              </div>
            </div>
          </CardSection>
        )}

        {/* Participants */}
        {participants.length > 0
          ? participants.map((participant, index) => {
              const idx = Number(participant.participantIndex) || index + 1;
              const isOrg = String(participant.type).toUpperCase() === "ORGANIZATION";

              // Get fields for this participant
              const fields = dynamicParticipantFields[index] || [];
              const participantType = String(participant.type).toUpperCase();

              // Skip participants with no fields
              if (fields.length === 0) return null;

              return (
                <CardSection
                  title={`Оролцогч - ${PARTICIPANT_TYPE_LABELS[participantType] || participantType} (${idx})`}
                  key={`participant-${index}`}
                >
                  <div className="rounded-lg border bg-white p-3 grid gap-3">
                    {fields.map((f, index) => (
                      <FieldRow
                        key={`${f.key}-${index}`}
                        field={f}
                        count={counts[f.key] ?? 0}
                        onInsert={handleFieldInsert}
                      />
                    ))}
                  </div>
                </CardSection>
              );
            })
          : null}

        {/* Document Fields/Placeholders - Organized by Category */}
        {Object.entries(placeholdersByCategory).map(([category, fields]) => {
          if (fields.length === 0) return null;

          return (
            <CardSection
              key={category}
              title={CATEGORY_LABELS[category] || category}
              defaultOpen={category === "CONTRACT"}
            >
              <div className="rounded-lg border bg-white p-3 grid gap-3">
                {fields.map((f) => (
                  <FieldRow
                    key={f.key}
                    field={f}
                    count={counts[f.key] ?? 0}
                    onInsert={handleFieldInsert}
                  />
                ))}
              </div>
            </CardSection>
          );
        })}

        

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <CardSection title="Баталгаажуулалтын алдаанууд" defaultOpen={true}>
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="text-sm font-medium text-red-800 mb-2">
                {validationErrors.length} алдаа олдлоо:
              </div>
              <ul className="text-sm text-red-700 space-y-1">
                {validationErrors.map((error, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">•</span>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardSection>
        )}

        <div className="mt-6 flex items-center gap-2">
          <button
            onClick={handleValidate}
            className="px-3 py-1.5 rounded bg-gray-600 text-white hover:bg-gray-700"
          >
            Баталгаажуулах
          </button>
          <button
            onClick={handleSave}
            className="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            Хадгалах
          </button>
        </div>
        <div id="status" className="mt-2 text-xs text-gray-600">
          {status}
        </div>
      </main>

      {/* Format Dialog */}
      {formatDialogOpen && selectedField && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">
              {selectedField.type === "DATE"
                ? "Огнооны формат сонгох"
                : selectedField.type === "CURRENCY"
                  ? "Мөнгөн дүнгийн формат сонгох"
                  : "Тооны формат сонгох"}
            </h3>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Placeholder: <span className="font-mono">{`{{${selectedField.key}}}`}</span>
              </p>
            </div>

            <div className="space-y-2 mb-6 max-h-96 overflow-y-auto">
              {selectedField.type === "DATE" ? (
                <>
                  <FormatOption
                    format="YYYY.MM.DD"
                    example="2024.01.15"
                    onSelect={handleFormatInsert}
                  />
                  <FormatOption
                    format="YYYY/MM/DD"
                    example="2024/01/15"
                    onSelect={handleFormatInsert}
                  />
                  <FormatOption
                    format="YYYY-MM-DD"
                    example="2024-01-15"
                    onSelect={handleFormatInsert}
                  />
                  <FormatOption
                    format="DD.MM.YYYY"
                    example="15.01.2024"
                    onSelect={handleFormatInsert}
                  />
                  <FormatOption
                    format="DD/MM/YYYY"
                    example="15/01/2024"
                    onSelect={handleFormatInsert}
                  />
                  <FormatOption
                    format="DD-MM-YYYY"
                    example="15-01-2024"
                    onSelect={handleFormatInsert}
                  />
                  <FormatOption
                    format="MM/DD/YYYY"
                    example="01/15/2024"
                    onSelect={handleFormatInsert}
                  />
                  <FormatOption
                    format="ll"
                    label="Монгол (товч)"
                    example="2024 оны 01 сарын 15"
                    onSelect={handleFormatInsert}
                  />
                  <FormatOption
                    format="LL"
                    label="Монгол (текст)"
                    example="2024 оны 1 дүгээр сарын 15"
                    onSelect={handleFormatInsert}
                  />
                  <FormatOption
                    format="LL-long"
                    label="Монгол (бүтэн)"
                    example="хоёр мянган хорин дөрөвдүгээр оны нэгдүгээр сарын арван тавны өдөр"
                    onSelect={handleFormatInsert}
                  />
                </>
              ): selectedField.type === "NUMBER" || selectedField.type === "CURRENCY" ? (
                <>
                  <FormatOption
                    format="default"
                    label="Анхдагч (форматгүй)"
                    example="1500000"
                    onSelect={handleFormatInsert}
                  />
                  <FormatOption
                    format="formatted"
                    label="Тоогоор (таслалтай)"
                    example="1,500,000"
                    onSelect={handleFormatInsert}
                  />
                  <FormatOption
                    format="mongolian"
                    label="Монгол үсгээр"
                    example="нэг сая таван зуун мянга"
                    onSelect={handleFormatInsert}
                  />
                  <FormatOption
                    format="mongolian_currency"
                    label="Монгол үсгээр (төгрөг)"
                    example="нэг сая таван зуун мянган төгрөг"
                    onSelect={handleFormatInsert}
                  />
                </>
              ) : null}
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={handleFormatDialogClose}
                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
              >
                Цуцлах
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Format option component
function FormatOption({
  format,
  label,
  example,
  onSelect,
}: {
  format: PlaceholderFormatType;
  label?: string;
  example: string;
  onSelect: (format: PlaceholderFormatType) => void;
}) {
  return (
    <button
      onClick={() => onSelect(format)}
      className="w-full text-left p-3 rounded border hover:bg-blue-50 hover:border-blue-500 transition-colors"
    >
      <div className="font-medium">{label || format}</div>
      <div className="text-xs text-gray-500 mt-1">{example}</div>
    </button>
  );
}
